import React, { useState, useEffect } from 'react';

import { Box, Backdrop, Typography, CircularProgress } from '@mui/material';

import { ActionsStep } from './ActionsStep';
import { ConditionsStep } from './ConditionsStep';
import { GeneralStep } from './GeneralStep';
import { WizardLayout } from './WizardLayout';
import { useUser } from '../../../../global/UserContext';
import GlobalModal from '../../../../modals/GlobalModal/GlobalModal';
import { automationApi } from '../../api/automationApi';
import { useRuleWizard } from '../../hooks/useRuleWizard';
import { dtoToDraft } from '../../mappers/rule.mapper';
import { useRuleStore } from '../../state/ruleStore';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';

import type { Plugin } from '../../hooks/useAutomationDefinitions';
import type { AutomationRuleDTO } from '../../types/dto';

type Props = {
  organizationUuid: string;
  plugin: Plugin;
  open: boolean;
  onClose: () => void;
  onCreated?: (ruleUuid: string) => void;
  editRuleUuid?: string;
};

export const CreateAutomationModal: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  editRuleUuid,
  organizationUuid,
  plugin,
}) => {
  const theme = useThemeContext();
  const { user } = useUser();
  const createdBy = user?.id ?? '';
  const {
    setGeneral,
    draft,
    reset,
    selectedTrigger,
    setSelectedTrigger,
    setPreviousTriggerUuid,
    setExistingTriggerUuid,
    setLoading,
    loading,
  } = useRuleStore();

  const { saveGeneral, saveConditions, saveActions, finalize } = useRuleWizard();

  // Estados para controle do modal
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [isCreating, setIsCreating] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [createdRuleId, setCreatedRuleId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Resetar e inicializar quando modal abre
  useEffect(() => {
    console.log('🔍 === useEffect [open, editRuleUuid] ===');
    console.log('🔍 open:', open);
    console.log('🔍 editRuleUuid:', editRuleUuid);

    if (!open) return; // Só inicializar quando abrir

    const isEdit = !!editRuleUuid;
    console.log('🔍 Modal abrindo - Modo:', isEdit ? 'EDIÇÃO' : 'CRIAÇÃO');

    setIsEditMode(isEdit);
    setStep(0);
    setIsCreating(false);
    setShowCancelModal(false);

    if (isEdit) {
      // Modo edição: setar UUID da regra existente
      console.log('🔍 Modo EDIÇÃO - UUID:', editRuleUuid);
      setCreatedRuleId(editRuleUuid);
    } else {
      // Modo criação: limpar TUDO primeiro
      console.log('🔍 Modo CRIAÇÃO - Limpando estados anteriores...');
      setCreatedRuleId(null);
      reset(); // ✅ LIMPAR TUDO!

      // Depois setar organization_uuid e created_by
      console.log('🔍 Setando organization_uuid:', organizationUuid);
      console.log('🔍 Setando created_by:', createdBy);
      setGeneral({
        organization_uuid: organizationUuid,
        created_by: createdBy,
      });
    }
  }, [open, editRuleUuid, organizationUuid, createdBy, setGeneral, reset]);

  // Hidratar dados para edição
  useEffect(() => {
    let live = true;
    const hydrate = async () => {
      if (open && editRuleUuid) {
        try {
          setLoading({ isHydrating: true, currentOperation: 'Carregando dados da regra...' });

          const dto: AutomationRuleDTO = await automationApi.getRule(editRuleUuid);
          if (!live) return;
          const d = dtoToDraft(dto);

          // Setar dados gerais
          setGeneral({
            id: dto.id,
            uuid: d.uuid,
            name: d.name,
            description: d.description,
            isActive: d.isActive,
            organization_uuid: d.organization_uuid,
            created_by: d.created_by,
            conditionsRoot: d.conditionsRoot,
            actions: d.actions || [],
          });

          // Hidratar trigger selecionado
          if (dto.triggers && dto.triggers.length > 0) {
            // Pegar o ÚLTIMO trigger (mais recente) caso haja múltiplos
            const trigger = dto.triggers[dto.triggers.length - 1];
            console.log('🔍 Hidratando trigger da regra:', trigger);
            console.log(`📊 Total de triggers: ${dto.triggers.length}`);

            // Buscar o trigger definition completo
            try {
              const { triggersApi } = await import('../../api/triggersApi');
              const triggerDef = await triggersApi.getTriggerDefinition(
                trigger.trigger_definition.uuid
              );
              console.log('✅ Trigger definition carregado:', triggerDef);

              // Setar trigger selecionado e rastrear estado
              setSelectedTrigger(triggerDef);
              setPreviousTriggerUuid(triggerDef.uuid);
              setExistingTriggerUuid(trigger.uuid); // UUID do trigger salvo no backend

              console.log('✅ Estado do trigger salvo:', {
                selectedTrigger: triggerDef.uuid,
                previousTriggerUuid: triggerDef.uuid,
                existingTriggerUuid: trigger.uuid,
              });
            } catch (error) {
              console.error('❌ Erro ao carregar trigger definition:', error);
              setSelectedTrigger(trigger.trigger_definition as any);
              setPreviousTriggerUuid(trigger.trigger_definition.uuid);
              setExistingTriggerUuid(trigger.uuid);
            }
          } else {
            console.log('⚠️ Nenhum trigger encontrado na regra');
            setSelectedTrigger(null);
            setPreviousTriggerUuid(null);
            setExistingTriggerUuid(null);
          }
        } catch (error) {
          console.error('Erro ao carregar regra para edição:', error);
        } finally {
          setLoading({ isHydrating: false, currentOperation: null });
        }
      }
    };
    hydrate();
    return () => {
      live = false;
    };
  }, [
    open,
    editRuleUuid,
    setGeneral,
    setSelectedTrigger,
    setPreviousTriggerUuid,
    setExistingTriggerUuid,
    setLoading,
  ]);

  // Resetar estado quando modal fecha
  useEffect(() => {
    if (!open) {
      console.log('🔍 Modal fechando - limpando estados...');
      setStep(0);
      setIsCreating(false);
      setShowCancelModal(false);
      setCreatedRuleId(null);
      setIsEditMode(false);
      reset(); // Limpa draft e selectedTrigger
    }
  }, [open, reset]);

  // Validações para cada aba
  const isGeneralValid = () => {
    console.log('🔍 === isGeneralValid ===');
    console.log('🔍 draft.name:', draft.name);
    console.log('🔍 draft.description:', draft.description);
    console.log('🔍 draft.organization_uuid:', draft.organization_uuid);
    console.log('🔍 selectedTrigger:', selectedTrigger);

    const isValid = !!(
      draft.name?.trim() &&
      draft.description?.trim() &&
      draft.organization_uuid &&
      selectedTrigger
    );

    console.log('🔍 isValid:', isValid);
    return isValid;
  };

  const isConditionsValid = () => {
    // Para criação: deve ter pelo menos uma condição
    // Para edição: sempre válido (pode ter 0 condições)
    if (!isEditMode && (!draft.conditionsRoot || draft.conditionsRoot.children.length === 0)) {
      return false;
    }
    return true;
  };

  const isActionsValid = () => {
    console.log('🔍 === isActionsValid ===');
    console.log('🔍 isEditMode:', isEditMode);
    console.log('🔍 draft.actions:', draft.actions);
    console.log('🔍 actions length:', draft.actions?.length);

    // Para criação: deve ter pelo menos uma ação
    // Para edição: sempre válido (pode ter 0 ações)
    if (!isEditMode && (!draft.actions || draft.actions.length === 0)) {
      console.log('❌ Validação falhou: modo criação sem ações');
      return false;
    }

    console.log('✅ Validação passou');
    return true;
  };

  // Debug: logar sempre que os valores mudarem
  useEffect(() => {
    console.log('🔍 === Estado do Draft ===');
    console.log('🔍 draft:', draft);
    console.log('🔍 selectedTrigger:', selectedTrigger);

    // Calcular validação inline para evitar warning de dependência
    const isValid = !!(
      draft.name?.trim() &&
      draft.description?.trim() &&
      draft.organization_uuid &&
      selectedTrigger
    );
    console.log('🔍 isGeneralValid():', isValid);
  }, [draft, selectedTrigger]);

  // Controle de navegação entre abas
  const handleTabChange = (newStep: number) => {
    // Se é modo de edição, pode navegar livremente
    if (isEditMode) {
      setStep(newStep as 0 | 1 | 2);
      return;
    }

    // Se é modo de criação, validar sequência
    if (newStep === 0) {
      setStep(0);
    } else if (newStep === 1) {
      // Só pode ir para condições se criou a regra
      if (createdRuleId) {
        setStep(1);
      }
    } else if (newStep === 2) {
      // Só pode ir para ações se criou a regra e tem condições
      if (createdRuleId && isConditionsValid()) {
        setStep(2);
      }
    }
  };

  const onNext = async () => {
    if (step === 0) {
      // Salvar dados gerais e criar regra
      if (!isGeneralValid()) return;

      setIsCreating(true);
      try {
        const ruleUuid = await saveGeneral({ createdBy, organizationUuid });
        setCreatedRuleId(ruleUuid);
        setStep(1);
      } catch (error) {
        console.error('Erro ao criar regra:', error);
      } finally {
        setIsCreating(false);
      }
    } else if (step === 1) {
      // Salvar condições
      if (!isConditionsValid()) return;

      try {
        await saveConditions();
        setStep(2);
      } catch (error) {
        console.error('Erro ao salvar condições:', error);
      }
    } else if (step === 2) {
      // ✅ NOVO: No step 2 (Ações), "Próximo" também salva as ações
      // (útil para modo edição onde pode navegar entre abas)
      try {
        console.log('🔍 Salvando ações no step 2...');
        await saveActions();
        console.log('✅ Ações salvas com sucesso');
      } catch (error) {
        console.error('❌ Erro ao salvar ações:', error);
      }
    }
  };

  const onBack = () => {
    if (step > 0) {
      setStep(prev => (prev - 1) as 0 | 1 | 2);
    }
  };

  const onFinish = async () => {
    console.log('🔍 === INÍCIO onFinish ===');
    console.log('🔍 isActionsValid():', isActionsValid());
    console.log('🔍 Draft actions:', draft.actions);
    console.log('🔍 Draft UUID:', draft.uuid);
    console.log('🔍 Draft ID:', draft.id);

    if (!isActionsValid()) {
      console.log('❌ Validação de ações falhou');
      return;
    }

    try {
      console.log('🔍 Chamando saveActions...');
      await saveActions();
      console.log('✅ saveActions concluído');

      console.log('🔍 Chamando finalize...');
      await finalize();
      console.log('✅ finalize concluído');

      console.log('🔍 Chamando onCreated...');
      onCreated?.(draft.uuid!);
      console.log('✅ onCreated concluído');

      console.log('🔍 Fechando modal...');
      onClose();
      console.log('✅ Modal fechado');
    } catch (error) {
      console.error('❌ Erro ao finalizar regra:', error);
    }
  };

  // Controle de cancelamento
  const handleClose = () => {
    // Se é modo de edição, pode fechar normalmente com refresh
    if (isEditMode) {
      onClose();
      onCreated?.(editRuleUuid || '');
      return;
    }

    // Se não criou a regra ainda (modo criação), pode fechar normalmente
    if (!createdRuleId) {
      onClose();
      return;
    }

    // Se já criou a regra (modo criação), mostrar modal de confirmação
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    try {
      // Se já criou a regra, deletar ela
      if (createdRuleId) {
        console.log('🗑️ Deletando regra criada:', createdRuleId);
        await automationApi.deleteRule(createdRuleId);
      }
    } catch (error) {
      console.error('Erro ao deletar regra durante cancelamento:', error);
    }

    // Fechar modal e refresh
    onClose();
    onCreated?.(createdRuleId || '');
  };

  const cancelCancel = () => {
    setShowCancelModal(false);
  };

  return (
    <>
      {/* Overlay de loading durante hidratação */}
      <Backdrop
        open={loading.isHydrating}
        sx={{
          color: theme.text.primary,
          zIndex: theme => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={32} sx={{ color: theme.primary }} />
          <Typography variant="body1" sx={{ color: theme.text.primary }}>
            {loading.currentOperation || 'Carregando...'}
          </Typography>
        </Box>
      </Backdrop>

      <WizardLayout
        open={open}
        step={step}
        steps={['Geral', 'Condições', 'Ações']}
        onBack={onBack}
        onNext={onNext}
        onClose={handleClose}
        onFinish={onFinish}
        onTabChange={handleTabChange}
        canNext={
          step === 0 ? isGeneralValid() && !isCreating : step === 1 ? isConditionsValid() : true
        }
        canFinish={isActionsValid()}
        statusActive={draft.isActive}
        onToggleActive={v => setGeneral({ isActive: v })}
        isCreating={isCreating}
        isEditMode={isEditMode}
      >
        {step === 0 && <GeneralStep plugin={plugin} value={draft} onChange={p => setGeneral(p)} />}
        {step === 1 && <ConditionsStep plugin={plugin} />}
        {step === 2 && <ActionsStep />}
      </WizardLayout>

      {/* Modal de confirmação para cancelamento */}
      <GlobalModal
        open={showCancelModal}
        title="Confirmação"
        description="Deseja realmente cancelar a criação da Regra de Automação? Todos os dados inseridos serão perdidos."
        onClose={cancelCancel}
        onConfirm={confirmCancel}
        onCancel={cancelCancel}
        confirmButtonText="Sim"
        cancelButtonText="Não"
        width="30rem"
        height="15rem"
        closeOnBackdropClick={false}
        closeOnEscapeKeyDown={false}
      />
    </>
  );
};

export default CreateAutomationModal;
